/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React from 'react';

/**
 * **What an authored empty value does where this expression is sent** (Todd, 2026-09-29, option (a),
 * from rehearsing `S3b`: an `Otherwise` left on *Send this value* with an empty box sent `cid=`).
 *
 * An authored empty value is an answer, and what the answer does differs by surface. Measured in the
 * drivers on 2026-09-29, not assumed:
 *
 * | Surface | An empty value | Where |
 * | --- | --- | --- |
 * | query parameter, VAST and ORTB | **sent**, as `name=` | `BaseDriver.forEachEffectiveParamField` pushes `''` |
 * | request header | **left off**; the driver's own header stands | `setHeader` skips `''` |
 * | ORTB body leaf | **left off**; the driver's own value stands | `openrtb.driver.ts` reads `''` as unresolved |
 * | response mapping | **left off**; the driver's own value stands | `coerceValue` returns `undefined` for blank |
 *
 * A context rather than a prop, for the tested scope's reason: a branch can sit any depth below the
 * editor that knows its destination. **`null` says nothing**: an editor that does not know where its
 * expression goes (the library page, a round override) makes no claim about it.
 */
export interface EmptyValueOnSurface {
  outcome: 'sent-empty' | 'left-off';
  /** The destination's name, for the sentence — `cid` reads `cid=`. */
  name: string;
}

export const EmptyValueContext = React.createContext<EmptyValueOnSurface | null>(null);

/** The sentence a branch shows under an empty *Send this value*, or `null` where there is none to say. */
export function emptyValueSentence(on: EmptyValueOnSurface | null): string | null {
  if (!on) return null;
  return on.outcome === 'sent-empty'
    ? `This sends ${on.name}= with an empty value. To leave ${on.name} off when this branch is taken, choose Never send it.`
    : 'An empty value is not sent here: it is left off, and whatever bidBrain builds for it stands. To remove that too, choose Never send it.';
}
